// Solutions page — structured environment data model.
// Add new environments by appending to ENVIRONMENTS with the same shape.

// Canonical flooring-system terminology (used across recommendations).
export const FLOORING_SYSTEMS = [
  "Entrance-Grade Carpet Tile",
  "Interior Carpet Tile",
  "Luxury Vinyl Plank",
  "Luxury Vinyl Tile — Stone and Concrete Looks",
  "Entrance Grid Systems",
  "Commercial Rubber Flooring",
  "Wall-to-Wall Broadloom",
  "Custom Branded Entrance Mats",
  "Slip-Resistant Flooring",
  "Anti-Fatigue Matting",
  "Adhesive Systems",
  "Transitions and Accessories",
  "Complete Installation Solutions",
];

// Design Resource Center categories. status: "available" | "coming" | "pending"
export const RESOURCE_CATEGORIES = [
  { name: "Product Specifications", status: "coming", note: "Specification Resource Coming Soon" },
  { name: "Installation Guides", status: "coming", note: "Installation Guide Coming Soon" },
  { name: "Maintenance Guides", status: "coming", note: "Maintenance Guide Coming Soon" },
  { name: "Sustainability Documentation", status: "pending", note: "Technical Data Pending" },
  { name: "Acoustic Information", status: "pending", note: "Technical Data Pending" },
  { name: "Fire and Slip Ratings", status: "pending", note: "Technical Data Pending" },
  { name: "Transition Details", status: "coming", note: "Specification Resource Coming Soon" },
  { name: "Adhesives and Accessories", status: "coming", note: "Specification Resource Coming Soon" },
];

const PENDING = "Product Collection To Be Confirmed";

// Maps a collection name to its product detail path "<categorySlug>/<productSlug>".
const PRODUCT_LINKS = {
  "Champion Super Nop Tile": "carpet-tile-goods/champion-super-nop-tile",
  "Interior Carpet Tile": "carpet-tile-goods/champion-super-nop-tile",
  "Champion Super Nop — Entrance": "broadloom-sheet-roll-goods/champion-super-nop",
  "Natures View™ Style Rustique": "luxury-vinyl-plank-tiles/natures-view-style-rustique-7-x-48",
  "Nature View™ Stone Look": "luxury-vinyl-plank-tiles/natures-view-style-rustique-18-x-18",
  Eagle: "broadloom-sheet-roll-goods/eagle",
  "Custom Branded Entrance Mats": "custom-logos/champion-super-nop-csn-customs",
  "Entrance Grid Systems": "entry-grid-systems/snap-trax-tile",
  "Snap Trax Tile": "entry-grid-systems/snap-trax-tile",
  "Comfort Step 42": "industrial-anti-fatigue-mats/comfort-step-42",
  "Elements Recycled Rubber": "elements-recycled-rubber-flooring/elements-100-series",
};

// Reusable collection card factory.
const c = (name, system, image, application, benefit, expanded = {}) => ({
  name,
  system,
  image,
  application,
  benefit,
  product: expanded.product || PRODUCT_LINKS[name] || null,
  fit: expanded.fit || "Selected to align with the design and operational priorities of this environment.",
  applications: expanded.applications || ["Primary circulation", "Entrances and transitions", "Supporting zones"],
  coordinating: expanded.coordinating || ["Transitions and Accessories", "Adhesive Systems"],
  transitions: expanded.transitions || "Coordinated transition profiles maintain flush, accessible thresholds between systems.",
  maintenance: expanded.maintenance || "Routine vacuuming and periodic low-moisture cleaning support long-term appearance.",
  resources: expanded.resources || ["Specification Resource Coming Soon", "Maintenance Guide Coming Soon"],
});

export const ENVIRONMENTS = [
  {
    slug: "corporate-workplaces",
    name: "Corporate Workplaces",
    image: "/products/csn/office.jpg",
    overview:
      "Corporate workplaces balance brand expression, employee wellbeing, and heavy daily circulation. Flooring sets the tone at the entrance, guides movement through open plans, and supports focus in meeting and collaboration zones.",
    priorities: [
      "A confident, on-brand first impression at reception and entrances",
      "Acoustic comfort across open-plan and collaborative areas",
      "Durable, easily maintained surfaces for high daily foot traffic",
      "Design flexibility to zone spaces and support wayfinding",
    ],
    challenges: [
      "Tracked-in dirt and moisture at building entrances",
      "Noise and echo in open floor plates",
      "Uneven wear along primary circulation paths",
      "Coordinating multiple materials across departments",
    ],
    recommended: [
      "Entrance-Grade Carpet Tile",
      "Interior Carpet Tile",
      "Luxury Vinyl Plank",
      "Entrance Grid Systems",
      "Custom Branded Entrance Mats",
      "Transitions and Accessories",
    ],
    collections: [
      c(
        "Champion Super Nop Tile",
        "Interior Carpet Tile",
        "/products/csn-tile/office.jpg",
        "Open-plan workstations & circulation",
        "Textured, modular surface that absorbs noise and wears evenly",
        {
          fit: "The modular format lets you zone open plans, refresh worn tiles individually, and carry brand color through the workplace.",
          applications: ["Open-plan workstations", "Corridors and circulation", "Collaboration zones"],
          coordinating: ["Luxury Vinyl Plank", "Transitions and Accessories", "Custom Branded Entrance Mats"],
          transitions: "Flush transition strips bridge carpet tile to LVP at café and meeting-room thresholds.",
          maintenance: "Spot-clean and low-moisture extraction keep the needle-punched surface looking crisp.",
          resources: ["Specification Resource Coming Soon", "CAD File Coming Soon", "Maintenance Guide Coming Soon"],
        }
      ),
      c(
        "Natures View™ Style Rustique",
        "Luxury Vinyl Plank",
        "/products/nv-7x48/toasted-oak.jpg",
        "Reception, café & meeting rooms",
        "Warm wood visuals with commercial durability and easy upkeep",
        {
          fit: "Realistic wood looks bring warmth to client-facing spaces while standing up to rolling chairs and heavy traffic.",
          applications: ["Reception & lobby", "Café and pantry", "Executive & meeting rooms"],
          coordinating: ["Interior Carpet Tile", "Transitions and Accessories", "Adhesive Systems"],
          transitions: "Reducers and T-moldings coordinate LVP with carpet tile and hard-surface changes.",
          maintenance: "Sweep and damp-mop; no wax or refinishing required.",
          resources: ["Specification Resource Coming Soon", "Installation Guide Coming Soon"],
        }
      ),
      c(
        "Champion Super Nop — Entrance",
        "Entrance-Grade Carpet Tile",
        "/products/csn/entrance.jpg",
        "Vestibules & building entrances",
        "Scrapes and holds dirt and moisture before it reaches interiors",
        {
          fit: "A robust entrance system protects downstream flooring and reduces slip hazards at the door.",
          applications: ["Vestibules", "Main entrances", "Elevator lobbies"],
          coordinating: ["Entrance Grid Systems", "Custom Branded Entrance Mats", "Interior Carpet Tile"],
          transitions: "Recessed and surface transitions integrate entrance systems with interior floors.",
          maintenance: "Daily vacuuming with periodic deep extraction maintains performance.",
          resources: ["Specification Resource Coming Soon", "Maintenance Guide Coming Soon"],
        }
      ),
    ],
    outcomes: [
      "A cohesive material story from entrance to workspace",
      "Quieter, more comfortable open-plan environments",
      "Lower lifecycle cost through modular replacement",
    ],
  },
  {
    slug: "healthcare",
    name: "Healthcare",
    image: "/products/csn/hospital.jpg",
    overview:
      "Healthcare settings demand hygienic, resilient, and quiet flooring that supports infection-control practices and continuous 24/7 use while remaining welcoming to patients and families.",
    priorities: ["Cleanability and hygienic surfaces", "Slip resistance in wet zones", "Acoustic comfort for rest and recovery"],
    challenges: ["Constant traffic and rolling equipment", "Moisture and spill exposure", "Strict cleaning-chemical resistance"],
    recommended: ["Luxury Vinyl Tile — Stone and Concrete Looks", "Slip-Resistant Flooring", "Interior Carpet Tile", "Entrance Grid Systems", "Transitions and Accessories"],
    collections: [
      c("Nature View™ Stone Look", "Luxury Vinyl Tile — Stone and Concrete Looks", "/products/nv-18x18/waiting.jpg", "Waiting & patient areas", "Clean stone visuals that are easy to sanitize"),
    ],
    outcomes: ["Hygienic, easily maintained surfaces", "Reduced slip risk in wet areas", "Calmer, quieter recovery spaces"],
  },
  {
    slug: "education",
    name: "Education",
    image: "/products/csn/school.jpg",
    overview:
      "Schools and universities need hard-wearing, budget-conscious flooring that handles peak-hour crowds, supports acoustics in learning spaces, and keeps entrances clean through every season.",
    priorities: ["Durability under peak foot traffic", "Acoustic support in classrooms", "Easy, low-cost maintenance"],
    challenges: ["Seasonal dirt and moisture at entries", "Heavy point loads from furniture", "Tight maintenance budgets"],
    recommended: ["Entrance-Grade Carpet Tile", "Interior Carpet Tile", "Luxury Vinyl Plank", "Entrance Grid Systems", "Complete Installation Solutions"],
    collections: [
      c("Champion Super Nop Tile", "Interior Carpet Tile", "/products/csn-tile/entrance.jpg", "Classrooms & corridors", "Durable, replaceable tiles that dampen noise"),
      c("Eagle", "Entrance-Grade Carpet Tile", "/products/eagle/1_grocery.jpg", "Entries & commons", "Traps dirt and moisture at high-use doors"),
    ],
    outcomes: ["Cleaner entrances year-round", "Quieter learning environments", "Simple tile-by-tile replacement"],
  },
  {
    slug: "hospitality",
    name: "Hospitality",
    image: "/products/spectra/restaurant.jpg",
    overview:
      "Hotels and restaurants use flooring to shape guest experience, blending refined aesthetics with the durability to handle luggage, dining service, and round-the-clock activity.",
    priorities: ["Memorable, refined guest impression", "Comfort underfoot and acoustic warmth", "Resilience in back-of-house and service"],
    challenges: ["High-visibility wear in lobbies", "Spills in dining and bar areas", "Continuous operation with no downtime"],
    recommended: ["Luxury Vinyl Plank", "Interior Carpet Tile", "Custom Branded Entrance Mats", "Slip-Resistant Flooring", "Transitions and Accessories"],
    collections: [
      c("Natures View™ Style Rustique", "Luxury Vinyl Plank", "/products/nv-7x48/sundried-teak.jpg", "Lobbies & guest areas", "Warm, upscale wood looks that resist wear"),
    ],
    outcomes: ["Elevated guest-facing spaces", "Safer service and bar zones", "Durability that protects the investment"],
  },
  {
    slug: "retail",
    name: "Retail",
    image: "/products/csn-tile/retail.jpg",
    overview:
      "Retail flooring directs shoppers, showcases merchandise, and endures relentless traffic while reinforcing brand identity from the entrance onward.",
    priorities: ["Brand-aligned aesthetics", "Traffic durability and easy cleaning", "Clean, safe entrances"],
    challenges: ["Tracked-in weather at entries", "Heavy, concentrated foot traffic", "Frequent layout changes"],
    recommended: ["Luxury Vinyl Plank", "Luxury Vinyl Tile — Stone and Concrete Looks", "Entrance-Grade Carpet Tile", "Custom Branded Entrance Mats", "Transitions and Accessories"],
    collections: [
      c("Natures View™ Style Rustique", "Luxury Vinyl Plank", "/products/nv-7x48/silver-sand.jpg", "Sales floors", "On-trend visuals with commercial toughness"),
      c("Custom Branded Entrance Mats", "Custom Branded Entrance Mats", "/products/csn-customs/entrance.jpg", "Store entrances", "Branded first impression that controls dirt"),
    ],
    outcomes: ["Stronger brand presence", "Cleaner, safer entrances", "Flexible, refreshable layouts"],
  },
  {
    slug: "government",
    name: "Government",
    image: "/products/csn-customs/reception.jpg",
    overview:
      "Government facilities require durable, accessible, and cost-effective flooring that performs under public traffic while supporting sustainability and long service life.",
    priorities: ["Long-term durability and value", "Accessibility and safety", "Sustainable material choices"],
    challenges: ["High public foot traffic", "Wide seasonal soil and moisture", "Long procurement and service cycles"],
    recommended: ["Entrance-Grade Carpet Tile", "Interior Carpet Tile", "Luxury Vinyl Tile — Stone and Concrete Looks", "Entrance Grid Systems", "Complete Installation Solutions"],
    collections: [
      c("Entrance Grid Systems", "Entrance Grid Systems", "/products/snaptrax/1_entrance.jpg", "Lobbies & vestibules", "Heavy-duty dirt and moisture control at entries"),
      c("Champion Super Nop Tile", "Interior Carpet Tile", "/products/csn-tile/grocery.jpg", "Corridors & offices", "Resilient modular surface with easy repair"),
    ],
    outcomes: ["Dependable performance under public use", "Accessible, safe transitions", "Predictable lifecycle value"],
  },
  {
    slug: "industrial",
    name: "Industrial",
    image: "/products/comfort-step-42/1_machine.jpg",
    overview:
      "Industrial environments prioritize worker safety and comfort, resisting heavy loads, moisture, and chemicals while reducing fatigue at standing workstations.",
    priorities: ["Worker comfort and fatigue reduction", "Slip and chemical resistance", "Load-bearing durability"],
    challenges: ["Long standing shifts", "Oil, grease, and chemical exposure", "Heavy rolling and point loads"],
    recommended: ["Anti-Fatigue Matting", "Slip-Resistant Flooring", "Commercial Rubber Flooring", "Entrance Grid Systems", "Complete Installation Solutions"],
    collections: [
      c("Comfort Step 42", "Anti-Fatigue Matting", "/products/comfort-step-42/2_warehouse.jpg", "Standing workstations", "Cushioned support that reduces fatigue"),
    ],
    outcomes: ["More comfortable, safer workstations", "Resistance to demanding conditions", "Lower long-term replacement cost"],
  },
  {
    slug: "grocery",
    name: "Grocery",
    image: "/products/nv-18x18/grocery.jpg",
    overview:
      "Grocery environments combine customer-facing aisles with demanding back-of-house and wet zones, requiring flooring that is safe, cleanable, and visually appealing.",
    priorities: ["Slip safety in wet and prep areas", "Clean, appealing sales-floor aesthetics", "Easy, frequent cleaning"],
    challenges: ["Moisture near produce and freezers", "Heavy carts and restocking traffic", "Continuous cleaning cycles"],
    recommended: ["Luxury Vinyl Tile — Stone and Concrete Looks", "Slip-Resistant Flooring", "Anti-Fatigue Matting", "Entrance-Grade Carpet Tile", "Transitions and Accessories"],
    collections: [
      c("Nature View™ Stone Look", "Luxury Vinyl Tile — Stone and Concrete Looks", "/products/nv-18x18/aisles.jpg", "Sales aisles", "Durable stone visuals that clean easily"),
    ],
    outcomes: ["Safer wet and prep areas", "Attractive, durable aisles", "Simple daily maintenance"],
  },
  {
    slug: "hardware-retail",
    name: "Hardware Retail",
    image: "/products/snaptrax/4_retail.jpg",
    overview:
      "Hardware and home-improvement retail sees heavy carts, bulky goods, and constant seasonal soil, demanding rugged flooring and strong entrance protection.",
    priorities: ["Rugged, high-load durability", "Effective entrance dirt control", "Low-maintenance surfaces"],
    challenges: ["Heavy carts and pallet traffic", "Tracked-in soil and moisture", "Abrasive debris on floors"],
    recommended: ["Entrance Grid Systems", "Luxury Vinyl Plank", "Commercial Rubber Flooring", "Entrance-Grade Carpet Tile", "Complete Installation Solutions"],
    collections: [
      c("Snap Trax Tile", "Entrance Grid Systems", "/products/snaptrax/5_sports.jpg", "Entrances & vestibules", "Interlocking grid that scrapes and drains"),
      c("Natures View™ Style Rustique", "Luxury Vinyl Plank", "/products/nv-7x48/sterling-ash.jpg", "Aisles & displays", "Tough wood-look surface for heavy use"),
    ],
    outcomes: ["Strong entrance protection", "Durable, cart-ready aisles", "Lower cleaning overhead"],
  },
  {
    slug: "senior-living",
    name: "Senior Living",
    image: "/products/csn-customs/residence.jpg",
    overview:
      "Senior living balances a residential, welcoming feel with safety, slip resistance, and acoustic comfort to support residents and caregivers alike.",
    priorities: ["Slip resistance and safe transitions", "Warm, residential aesthetics", "Acoustic comfort and cleanability"],
    challenges: ["Fall-risk reduction", "Mobility-device traffic", "Spill and moisture management"],
    recommended: ["Luxury Vinyl Plank", "Slip-Resistant Flooring", "Interior Carpet Tile", "Transitions and Accessories", "Complete Installation Solutions"],
    collections: [
      c("Natures View™ Style Rustique", "Luxury Vinyl Plank", "/products/nv-7x48/dove.jpg", "Resident rooms & commons", "Warm, homelike wood looks that are easy to clean"),
      c("Interior Carpet Tile", "Interior Carpet Tile", "/products/csn-tile/office.jpg", "Lounges & corridors", "Comfort and quiet with modular repair"),
    ],
    outcomes: ["Safer, flush transitions", "Comfortable, homelike spaces", "Easy spot maintenance"],
  },
  {
    slug: "sports-recreation",
    name: "Sports & Recreation",
    image: "/products/csn/fitness.jpg",
    overview:
      "Fitness and recreation facilities need resilient, shock-absorbing, and slip-safe flooring that withstands equipment, moisture, and intense activity.",
    priorities: ["Impact absorption and resilience", "Slip safety in active and wet zones", "Durability under equipment loads"],
    challenges: ["Dropped weights and equipment", "Moisture in locker and pool areas", "Constant high-energy use"],
    recommended: ["Commercial Rubber Flooring", "Slip-Resistant Flooring", "Entrance Grid Systems", "Anti-Fatigue Matting", "Complete Installation Solutions"],
    collections: [
      c("Elements Recycled Rubber", "Commercial Rubber Flooring", "/products/comfort-step-42/1_machine.jpg", "Weight & fitness floors", "Shock-absorbing, resilient rubber surface"),
    ],
    outcomes: ["Protected subfloors and equipment", "Safer wet and active areas", "Long-lasting resilience"],
  },
  {
    slug: "public-spaces",
    name: "Public Spaces",
    image: "/products/snaptrax/2_entrance.jpg",
    overview:
      "Transit hubs, civic buildings, and cultural venues move large crowds and need durable, wayfinding-friendly flooring with strong entrance protection.",
    priorities: ["Crowd-scale durability", "Wayfinding through material zoning", "Robust entrance dirt control"],
    challenges: ["Extreme peak foot traffic", "Heavy soil and moisture load", "Continuous public access"],
    recommended: ["Entrance-Grade Carpet Tile", "Entrance Grid Systems", "Luxury Vinyl Tile — Stone and Concrete Looks", "Interior Carpet Tile", "Transitions and Accessories"],
    collections: [
      c("Entrance Grid Systems", "Entrance Grid Systems", "/products/snaptrax/3_entrance.jpg", "Main entrances", "High-capacity dirt and moisture capture"),
      c("Nature View™ Stone Look", "Luxury Vinyl Tile — Stone and Concrete Looks", "/products/nv-18x18/office.jpg", "Concourses & halls", "Durable stone visuals for large spans"),
    ],
    outcomes: ["Reliable performance at scale", "Clear circulation and wayfinding", "Protected interior surfaces"],
  },
  {
    slug: "mixed-use-developments",
    name: "Mixed-Use Developments",
    image: "/products/csn-customs/excellence.jpg",
    overview:
      "Mixed-use developments span residential, retail, and workplace zones, requiring a coordinated flooring palette that transitions gracefully across varied uses.",
    priorities: ["Cohesive palette across uses", "Durable shared-amenity surfaces", "Seamless transitions between zones"],
    challenges: ["Varied performance needs per zone", "Shared-entrance soil control", "Consistent design language"],
    recommended: ["Luxury Vinyl Plank", "Interior Carpet Tile", "Entrance-Grade Carpet Tile", "Luxury Vinyl Tile — Stone and Concrete Looks", "Transitions and Accessories"],
    collections: [
      c("Natures View™ Style Rustique", "Luxury Vinyl Plank", "/products/nv-7x48/toasted-oak.jpg", "Residential & amenity", "Warm, versatile wood looks across zones"),
      c("Champion Super Nop Tile", "Interior Carpet Tile", "/products/csn-tile/retail.jpg", "Workplace & lobbies", "Modular surface that unifies shared spaces"),
    ],
    outcomes: ["A unified material story", "Durable shared amenities", "Graceful zone-to-zone transitions"],
  },
];

export const getEnvironment = (slug) => ENVIRONMENTS.find((e) => e.slug === slug) || ENVIRONMENTS[0];
export const getEnvironmentBySlug = (slug) => ENVIRONMENTS.find((e) => e.slug === slug) || null;
export const PENDING_COLLECTION = PENDING;

// Curated real-project photo strips per environment ("See it in a real project").
export const ENV_GALLERIES = {
  "corporate-workplaces": [
    "/products/csn-customs/office.jpg",
    "/products/csn-customs/reception.jpg",
    "/products/nv-7x48/toasted-oak.jpg",
    "/products/csn-tile/retail.jpg",
  ],
  healthcare: [
    "/products/nv-18x18/waiting.jpg",
    "/products/spectra/waiting.jpg",
    "/products/spectra/reception.jpg",
    "/products/csn/hospital.jpg",
  ],
  education: [
    "/products/csn-tile/entrance.jpg",
    "/products/eagle/1_grocery.jpg",
    "/products/csn/fitness.jpg",
    "/products/csn-tile/office.jpg",
  ],
  hospitality: [
    "/products/spectra/restaurant.jpg",
    "/products/nv-7x48/sundried-teak.jpg",
    "/products/nv-7x48/toasted-oak.jpg",
    "/products/spectra/reception.jpg",
  ],
  retail: [
    "/products/csn-tile/retail.jpg",
    "/products/nv-7x48/silver-sand.jpg",
    "/products/csn-customs/entrance.jpg",
    "/products/snaptrax/4_retail.jpg",
  ],
  government: [
    "/products/csn-customs/reception.jpg",
    "/products/snaptrax/1_entrance.jpg",
    "/products/csn-tile/grocery.jpg",
    "/products/spectra/reception.jpg",
  ],
  industrial: [
    "/products/comfort-step-42/1_machine.jpg",
    "/products/comfort-step-42/2_warehouse.jpg",
    "/products/safestep/5_product.jpg",
    "/products/comfort-step-diamond/1_autoshop.jpg",
  ],
  grocery: [
    "/products/nv-18x18/grocery.jpg",
    "/products/nv-18x18/aisles.jpg",
    "/products/nv-18x18/bakery.jpg",
    "/products/kitchen-mat/2_kitchen.jpg",
  ],
  "hardware-retail": [
    "/products/snaptrax/4_retail.jpg",
    "/products/snaptrax/5_sports.jpg",
    "/products/csn-tile/hardware.jpg",
    "/products/nv-7x48/sterling-ash.jpg",
  ],
  "senior-living": [
    "/products/csn-customs/residence.jpg",
    "/products/nv-7x48/dove.jpg",
    "/products/csn-tile/office.jpg",
    "/products/spectra/waiting.jpg",
  ],
  "sports-recreation": [
    "/products/csn/fitness.jpg",
    "/products/comfort-step-42/1_machine.jpg",
    "/products/elements-200-series/1_training-center.jpg",
    "/products/snaptrax/5_sports.jpg",
  ],
  "public-spaces": [
    "/products/snaptrax/2_entrance.jpg",
    "/products/snaptrax/3_entrance.jpg",
    "/products/nv-18x18/office.jpg",
    "/products/spectra/layers.jpg",
  ],
  "mixed-use-developments": [
    "/products/csn-customs/excellence.jpg",
    "/products/nv-7x48/toasted-oak.jpg",
    "/products/csn-tile/retail.jpg",
    "/products/csn-customs/residence.jpg",
  ],
};
